import { useEffect, useState } from "react";

import {
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  IconButton,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { FaCheck, FaLightbulb, FaTimes } from "react-icons/fa";

import { fetchGroupMemberDetails, RecommendationEntry, SupplementHistoryEntry } from "~/api/members";
import { useAppState } from "~/hooks/useAppState";
import { useAppStore } from "~/store";
import { useDebugStore } from "~/store/debugStore";
import { invalidateMemberRecommendations, onMemberChanged, onPhAnalysisCompleted } from "~/utils/cacheInvalidation";

// Helper for formatting date strings
const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateStr;
  }
};

// Helper to sort recommendations by supplement name and then by dosage amount
const sortRecommendations = (recommendations: RecommendationEntry[] | null | undefined): RecommendationEntry[] => {
  if (!recommendations || !Array.isArray(recommendations) || recommendations.length === 0) {
    return [];
  }

  return [...recommendations].sort((a, b) => {
    // First sort by supplement name
    const supplementA = a.dosage.supplement.toLowerCase();
    const supplementB = b.dosage.supplement.toLowerCase();

    if (supplementA !== supplementB) {
      return supplementA.localeCompare(supplementB);
    }

    // If supplement names are the same, sort by amount (if available)
    const amountA = a.dosage.amount || 0;
    const amountB = b.dosage.amount || 0;

    return amountA - amountB;
  });
};

// Mock recommendations data for empty state visualization
const mockRecommendations: RecommendationEntry[] = [
  {
    id: 2001,
    dosage: {
      id: 1001,
      supplement: "Vitamin B12",
      amount: 500,
      unit: "mcg",
      frequency: "daily",
      note: null
    },
    contextType: "ph_analysis",
    contextId: 1,
    active: true,
    createdAt: new Date().toISOString()
  },
  {
    id: 2002,
    dosage: {
      id: 1002,
      supplement: "Baking Soda",
      amount: 1,
      unit: "tsp",
      frequency: "weekly",
      note: null
    },
    contextType: "ph_analysis",
    contextId: 1,
    active: true,
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
  },
  {
    id: 2003,
    dosage: {
      id: 1003,
      supplement: "Alkaline Calcium",
      amount: 2,
      unit: "pills",
      frequency: "twice_daily",
      note: "Take with meals"
    },
    contextType: "rbti_analysis",
    contextId: 2,
    active: true,
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
  }
];

// Sort the mock recommendations
const sortedMockRecommendations = sortRecommendations(mockRecommendations);

interface MemberRecommendationsProps {
  recommendations: RecommendationEntry[] | null | undefined;
  currentSupplements: SupplementHistoryEntry[] | null | undefined;
}

const MemberRecommendations = ({ recommendations = [], currentSupplements = [] }: MemberRecommendationsProps) => {
  const currentMember = useAppStore((state) => state.currentMember);
  const { userHomeData } = useAppState("userHomeData");
  const currentGroupMember = useAppStore((state) => state.currentGroupMember);
  const setCurrentGroupMember = useAppStore((state) => state.setCurrentGroupMember);
  const tallyRender = useDebugStore((state) => state.tallyRender);
  const [showMockData, setShowMockData] = useState(false);
  const [showAllRecommendations, setShowAllRecommendations] = useState(false);
  const [recommendationStatus, setRecommendationStatus] = useState<Record<number, boolean>>({});
  const toast = useToast();
  const queryClient = useQueryClient();

  // Update render tally on each render
  useEffect(() => {
    tallyRender("Recommendations");
  }, [tallyRender]);

  // Color mode values
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("green.100", "green.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const headerBg = useColorModeValue("green.50", "green.900");
  const emptyTextColor = useColorModeValue("gray.500", "gray.400");
  const iconColor = useColorModeValue("green.500", "green.300");

  // Set up event listeners for pH analysis completion and member changes
  useEffect(() => {
    console.log("Setting up event listeners for pH analysis completion and member changes");

    // Listen for pH analysis completed events
    const phAnalysisCleanup = onPhAnalysisCompleted((groupMemberId) => {
      console.log("pH analysis completed event received", { groupMemberId });
      invalidateMemberRecommendations(queryClient, groupMemberId);
    });

    // Listen for member changed events
    const memberChangedCleanup = onMemberChanged((groupMemberId) => {
      console.log("Member changed event received", { groupMemberId });
      invalidateMemberRecommendations(queryClient, groupMemberId);
    });

    // Clean up event listeners
    return () => {
      phAnalysisCleanup();
      memberChangedCleanup();
    };
  }, [queryClient]);

  // Fetch the current group member when currentMember or defaultGroup changes
  useEffect(() => {
    const fetchGroupMember = async () => {
      if (!currentMember || !userHomeData?.defaultGroup) {
        return;
      }

      try {
        console.log(
          "MemberRecommendations: fetching group member details for member",
          currentMember.id,
          "in group",
          userHomeData.defaultGroup.id
        );
        const groupMember = await fetchGroupMemberDetails(currentMember.id, userHomeData.defaultGroup.id);
        if (groupMember) {
          setCurrentGroupMember(groupMember);
        }
      } catch (err) {
        console.error("Error fetching group member:", err);
      }
    };

    fetchGroupMember();
  }, [currentMember, userHomeData, setCurrentGroupMember]);

  // Sort recommendations safely
  const safeRecommendations = Array.isArray(recommendations) ? recommendations : [];
  const sortedRecommendations = sortRecommendations(safeRecommendations);

  // Safe getter for current supplements
  const safeCurrentSupplements = Array.isArray(currentSupplements) ? currentSupplements : [];

  // Function to check if a supplement is currently being taken
  const isCurrentlyTaken = (recommendation: RecommendationEntry): boolean => {
    // Safe check to avoid errors
    if (!recommendation || !recommendation.dosage || !recommendation.dosage.supplement) {
      return false;
    }

    const recName = recommendation.dosage.supplement.toLowerCase();

    // Check against current supplements
    return safeCurrentSupplements.some(
      (supp) => supp.dosage && supp.dosage.supplement && supp.dosage.supplement.toLowerCase() === recName
    );
  };

  // Format dosage information for display
  const formatDosage = (amount?: number | null, unit?: string | null, frequency?: string | null) => {
    const parts: string[] = [];

    if (amount !== undefined && amount !== null) {
      parts.push(`${amount}`);
    }

    if (unit) {
      parts.push(unit);
    }

    if (frequency) {
      const formattedFrequency = frequency
        .replace("_", " ")
        .replace(/daily/i, "daily")
        .replace(/weekly/i, "weekly");
      parts.push(formattedFrequency);
    }

    return parts.join(" ");
  };

  // Format context type for display
  const formatContextType = (contextType: string) => {
    switch (contextType) {
      case "ph_analysis":
        return "pH Analysis";
      case "rbti_analysis":
        return "RBTI Analysis";
      default:
        return contextType.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase());
    }
  };

  // Handle toggling the recommendation status
  const toggleRecommendationStatus = (recommendationId: number) => {
    setRecommendationStatus((prev) => ({
      ...prev,
      [recommendationId]: !prev[recommendationId]
    }));
  };

  // Get recommendation status
  const getRecommendationStatus = (recommendationId: number): boolean => {
    return recommendationStatus[recommendationId] || false;
  };

  // Filter recommendations based on settings
  const getFilteredRecommendations = () => {
    if (!Array.isArray(sortedRecommendations)) {
      return [];
    }

    // If showing all recommendations, return them sorted
    if (showAllRecommendations) {
      return sortedRecommendations;
    }

    // Otherwise, filter out recommendations for supplements that are already being taken
    return sortedRecommendations.filter((recommendation) => !isCurrentlyTaken(recommendation));
  };

  // Show mock data if requested
  const recommendationsToShow = showMockData ? sortedMockRecommendations : getFilteredRecommendations();

  // Check if we should show empty state
  const showEmpty = !showMockData && (!Array.isArray(recommendations) || recommendations.length === 0);

  // Render the recommendations table
  const renderRecommendationsTable = (recommendations: RecommendationEntry[], isMock = false) => {
    if (!Array.isArray(recommendations) || recommendations.length === 0) {
      return (
        <Flex direction="column" align="center" justify="center" py={6}>
          <FaLightbulb color={emptyTextColor} size="3em" opacity={0.5} />
          <Text fontSize="md" color={emptyTextColor} mt={4} textAlign="center">
            No recommendations at this time
          </Text>
          <Text fontSize="sm" color={emptyTextColor} mt={2} textAlign="center">
            Recommendations will appear here after a pH analysis or visit with a practitioner.
          </Text>
        </Flex>
      );
    }

    return (
      <Table size="sm" variant="simple">
        <Thead bg={useColorModeValue("gray.50", "gray.700")}>
          <Tr>
            <Th>Recommendation</Th>
            <Th>Dosage</Th>
            <Th>Source</Th>
            <Th>Date</Th>
            <Th width="100px">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {recommendations.map((recommendation) => {
            const isActive = isCurrentlyTaken(recommendation);

            return (
              <Tr key={recommendation.id}>
                <Td fontWeight="medium">{recommendation.dosage.supplement}</Td>
                <Td>
                  {formatDosage(
                    recommendation.dosage.amount,
                    recommendation.dosage.unit,
                    recommendation.dosage.frequency
                  )}
                </Td>
                <Td>{formatContextType(recommendation.contextType)}</Td>
                <Td>{formatDate(recommendation.createdAt)}</Td>
                <Td>
                  <Flex justify="center" align="center" gap={2}>
                    <Tooltip label="Already taking" isDisabled={!isActive}>
                      <IconButton
                        aria-label="Taking"
                        icon={<FaCheck />}
                        size="xs"
                        colorScheme="green"
                        variant={isActive ? "solid" : "outline"}
                        isDisabled={true}
                      />
                    </Tooltip>
                    <Tooltip label="Not taking" isDisabled={isActive}>
                      <IconButton
                        aria-label="Not taking"
                        icon={<FaTimes />}
                        size="xs"
                        colorScheme="red"
                        variant={!isActive ? "solid" : "outline"}
                        isDisabled={true}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    );
  };

  return (
    <Card
      bg={cardBg}
      borderColor={borderColor}
      borderWidth="2px"
      borderRadius="lg"
      boxShadow="lg"
      minHeight="300px"
      marginBottom="1rem"
    >
      <CardHeader bg={headerBg} py={4} px={5} borderBottom="1px" borderColor={borderColor}>
        <Flex justify="space-between" align="center">
          <Flex align="center">
            <Heading size="md" color={textColor}>
              Recommended Supplements
            </Heading>
            <Tooltip label={showMockData ? "Using demo data" : "Show health recommendations"}>
              <IconButton
                aria-label="Supplement Recommendations"
                icon={<FaLightbulb />}
                size="sm"
                variant="ghost"
                colorScheme="green"
                ml={2}
              />
            </Tooltip>
          </Flex>
          <HStack>
            <Text fontSize="sm" mr={2}>
              Show All
            </Text>
            <Switch
              isChecked={showAllRecommendations}
              onChange={() => setShowAllRecommendations(!showAllRecommendations)}
              colorScheme="green"
              size="sm"
            />
          </HStack>
        </Flex>
      </CardHeader>

      <CardBody>{renderRecommendationsTable(recommendationsToShow, showMockData)}</CardBody>
    </Card>
  );
};

export default MemberRecommendations;
